/**
 * A TÖBBFŐS KIMUTATÁS-TÁBLÁZAT RENDEZÉSE ÉS NÉVSZŰRÉSE [Web UX 3. csomag (C), 2026-10-04]
 *
 * MI VOLT A BAJ (`Documents/WEB_UX_JAVASLATOK_2026-10-02.md` 1.4): a „Minden felhasználó"
 * táblázat (`components/Reports.tsx`, a fejléc a „Név" oszloppal kezdődik) a szerver sorrendjében
 * jött (`ReportsController.cs` `OrderByDescending(u => u.ParticipationRate)`), nem lehetett
 * rendezni és keresni. Egy 100+ soros edzői listában a „kinek tartozunk a legtöbbel?" kérdésre
 * csak végiggörgetéssel lehetett választ kapni.
 *
 * A SZABÁLY (tiszta függvények, hogy önállóan tesztelhetők legyenek — `Reports.sort.test.tsx`):
 *  • A rendezési érték PONTOSAN azt a mennyiséget követi, amit a cella KIÍR. Ahol a cella „-"-t
 *    (`NO_AMOUNT_TEXT`) vagy „Nincs adat"-ot ír, ott az érték `null`, és a `null` a lista VÉGÉRE
 *    kerül, IRÁNYTÓL FÜGGETLENÜL — egy hiányzó adat se a „legkisebb", se a „legnagyobb" nem lehet.
 *    A 0 Ft viszont VALÓDI 0 (a `formatForint` is „0 Ft"-ot ír ki rá, nem „-"-t), tehát a számok
 *    közé rendeződik.
 *  • Nevek: `localeCompare(…, 'hu')` (a „Cs"/„Sz"/„Zs" kettős betűk és az ékezetek a magyar
 *    ábécé szerint). Egyenlő értéknél a név dönt (stabil, kiszámítható sorrend).
 *  • „Utolsó tevékenység": a szerver `yyyy-MM-dd` dátum-csak szöveget küld
 *    (`ReportsController.cs` `LastActivity = … .ToString("yyyy-MM-dd")`), ez SZÖVEGKÉNT is helyesen
 *    rendeződik — `new Date('yyyy-MM-dd')`-t (UTC éjfél) szándékosan nem használunk.
 */
import { MultiUserReport } from '../types/report';
import { normalizeForSearch } from './nameSearch';

export type SortDirection = 'asc' | 'desc';

export type MultiReportSortKey =
  | 'name'
  | 'totalEvents'
  | 'attendedEvents'
  | 'participationRate'
  | 'totalHours'
  | 'verifiedHours'
  | 'hourlyRate'
  | 'totalEarnings'
  | 'totalCollected'
  | 'amountDue'
  | 'trainingPaidOut'
  | 'extraPaidOut'
  | 'balance'
  | 'deletedEvents'
  | 'paidEvents'
  | 'paymentRate'
  | 'totalPaymentAmount'
  | 'lastActivity';

export interface MultiReportSort {
  key: MultiReportSortKey;
  direction: SortDirection;
}

/** Pénz-érték úgy, ahogy a `formatForint` kezeli: null/undefined/NaN = nincs adat („-"). */
const money = (v: number | null | undefined): number | null =>
  v === null || v === undefined || Number.isNaN(v) ? null : v;

/** Darabszám, ahol a cella a hiányt is számként írja ki (`{x}` vagy `{x || 0}`). */
const count = (v: number | null | undefined): number | null =>
  v === null || v === undefined || Number.isNaN(v) ? null : v;

/**
 * A cella RENDEZÉSI értéke. Minden ág a `Reports.tsx` megfelelő cellájának kiírását követi —
 * ha a cella kiírása változik, EZT is át kell vezetni (a `Reports.sort.test.tsx` a felületen
 * keresztül méri, hogy a „-" a végére kerül).
 */
export function multiReportSortValue(u: MultiUserReport, key: MultiReportSortKey): number | string | null {
  switch (key) {
    case 'name':
      return u.name ?? '';
    case 'totalEvents':
      return count(u.totalEvents);
    case 'attendedEvents':
      return count(u.attendedEvents);
    case 'participationRate':
      // A cella 0 eseménynél „-"-t ír (nincs mit mérni) — lásd a `Reports.tsx` B-W3 kommentjét.
      return u.totalEvents ? (u.participationRate || 0) : null;
    case 'totalHours':
      return u.totalHours || 0; // a cella `${totalHours || 0} óra`
    case 'verifiedHours':
      return u.verifiedHours || 0; // a cella `${verifiedHours || 0} óra`
    case 'hourlyRate':
      return money(u.hourlyRate);
    case 'totalEarnings':
      return money(u.totalEarnings);
    case 'totalCollected':
      return money(u.totalCollected);
    case 'amountDue':
      return money(u.amountDue);
    case 'trainingPaidOut':
      return money(u.trainingPaidOut);
    case 'extraPaidOut':
      return money(u.extraPaidOut);
    case 'balance':
      return money(u.balance);
    case 'deletedEvents':
      // A cella 0 törölt eseménynél „-"-t ír → a végére kerül, mint minden más „-".
      return (u.deletedEventsTotalCount ?? 0) > 0 ? (u.deletedEventsTotalCount as number) : null;
    case 'paidEvents':
      return u.paidEvents || 0; // a cella `{paidEvents || 0}`
    case 'paymentRate':
      // A cella 0 JELENLÉTNÉL „-"-t ír — a fizetési arány nevezője a jelenlét (B-W3).
      return u.attendedEvents ? (u.paymentRate || 0) : null;
    case 'totalPaymentAmount':
      return money(u.totalPaymentAmount);
    case 'lastActivity':
      return u.lastActivity ? u.lastActivity : null; // a cella „Nincs adat"
    default:
      return null;
  }
}

const compareNames = (a: MultiUserReport, b: MultiUserReport) =>
  (a.name ?? '').localeCompare(b.name ?? '', 'hu');

/**
 * Rendezett MÁSOLAT (a bemenetet nem módosítja). `sort === null` → a szerver sorrendje marad.
 * A `null` értékű (a felületen „-"/„Nincs adat") sorok MINDKÉT irányban a végére kerülnek.
 */
export function sortMultiUserReport(
  rows: MultiUserReport[],
  sort: MultiReportSort | null,
): MultiUserReport[] {
  if (!sort) return rows.slice();
  const sign = sort.direction === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const va = multiReportSortValue(a, sort.key);
    const vb = multiReportSortValue(b, sort.key);
    const aMissing = va === null;
    const bMissing = vb === null;
    if (aMissing || bMissing) {
      if (aMissing && bMissing) return compareNames(a, b);
      return aMissing ? 1 : -1; // a hiányzó MINDIG a végére — az iránytól függetlenül
    }
    let cmp: number;
    if (typeof va === 'number' && typeof vb === 'number') {
      cmp = va - vb;
    } else {
      cmp = String(va).localeCompare(String(vb), 'hu');
    }
    if (cmp !== 0) return cmp * sign;
    return compareNames(a, b);
  });
}

/** Az első kattintás iránya: a név ábécérendben (növekvő), minden szám a legnagyobbal kezdve. */
export const defaultSortDirection = (key: MultiReportSortKey): SortDirection =>
  key === 'name' ? 'asc' : 'desc';

/**
 * Kis- és ékezet-érzéketlen összevetési alak („Ödön" ~ „odon", „ÁCS" ~ „acs") — a KÖZÖS segéd
 * (`utils/nameSearch.ts`) [1. validátor-kör M6]: a jelenléti ív névszűrője is ezt használja.
 * Itt csak továbbexportáljuk, hogy a meglévő importálók ne törjenek.
 */
export { normalizeForSearch };

/** Név szerinti kliens-oldali szűrés a BETÖLTÖTT listán (üres keresés → minden sor). */
export function filterMultiUserReportByName(rows: MultiUserReport[], query: string): MultiUserReport[] {
  const q = normalizeForSearch(query);
  if (!q) return rows;
  return rows.filter((u) => normalizeForSearch(u.name ?? '').includes(q));
}
